/**
 * The URL is the scope — ch00's half of the house pattern (`CLAUDE.md` §3.4).
 *
 * ── WHY THIS CHAPTER SUDDENLY HAS ONE ───────────────────────────────────────
 *
 * Every foundations read until now was a collection over the whole estate: schemes,
 * nodes, scenarios, plan versions, alerts, feeds. None needed a parameter the front end
 * could not guess, so ch00 had no scope module and rightly so.
 *
 * The parameter registry does. `GET /api/v1/registry/parameters` serves "the rows in
 * force on `as_of` — `valid_from <= as_of < valid_to`", and the contract's own docstring
 * says why the date is an argument rather than a clock read:
 *
 *   "`as_of` is required, not defaulted to today. G9 makes a stored value a claim about
 *    a moment, and 'what did the engine read last Tuesday' is only a query if the date is
 *    an argument; a read that defaulted to the clock would answer a different question on
 *    every call and would make a support conversation about a July run unanswerable."
 *
 * So the date is an argument, and it must be shareable: a planner asking
 * "which value was in force when that markdown ran" needs to be able to send the link.
 *
 * ── THE FOUR RULES, AS THEY APPLY HERE ──────────────────────────────────────
 *
 * 1. **Default to today — amended 2026-09-27 (owner, F11; `docs/adr/2002`).** This rule used
 *    to read "no default, ever". The owner overruled it for this screen: with no `?asOf=` the
 *    registry opens on today's date (UTC, the calendar the registry's own dates use) and writes
 *    it onto the link with `replaceState`, so the view is still a shareable, bookmarkable URL
 *    that names its date. The contract publishes no "latest date with data" read, and today is
 *    the date on which the rows the engine reads now are in force. A date the planner picks, or
 *    one a link carries, always wins.
 * 2. **The request carries the date it answers.** The default is resolved before the read,
 *    never inferred by the service.
 * 3. **A designed state, not an empty table.** "No rows were in force on that date" is a
 *    finding about the register and says so.
 * 4. **Parse from a string, not from `window`.** `readFoundationsScope` takes a search
 *    string, so the test asserts on a string and the export resolver can be handed the
 *    one it is given (`ExportScope`, `docs/adr/0017`).
 *
 * `?asOf=` is a query-string key in this chapter's own screens. ch04 already uses the
 * same spelling for its own as-at reads; the two never share a screen and no shared file
 * changes to add either.
 */

import { useEffect, useMemo, useState } from 'react';

/** Which date the registry is read as at — `?asOf=`. An ISO `YYYY-MM-DD`. */
export const AS_OF_PARAM = 'asOf';

export interface FoundationsScope {
  readonly asOf: string | null;
}

/** One parameter, trimmed, with blank treated as absent. */
function readParam(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * An ISO calendar date and nothing else.
 *
 * A malformed `?asOf=` is treated as absent rather than sent. The register would answer
 * a 422 for it, which is a failure surface describing a typo — and opening on today's date
 * (the screen's default, ADR 2002) is the more useful answer to a truncated link.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The scope a search string states. Pure: no `window`, no defaults, no guesses — the
 *  registry screen's default date is applied by the screen (`useDefaultAsOf`), not here. */
export function readFoundationsScope(search: string): FoundationsScope {
  const asOf = readParam(new URLSearchParams(search), AS_OF_PARAM);
  return { asOf: asOf !== null && ISO_DATE.test(asOf) ? asOf : null };
}

/**
 * Which key's exceptions the registry screen is showing — `?parameter=` (`docs/adr/2004`).
 *
 * The URL is the state (G2.2): "the exceptions and the effective value of
 * `exit.terminal_stock_ratio` on 2026-09-28" is a link a planner can send. No default — a
 * screen with no key selected shows the catalogue and nothing beneath it.
 */
export const PARAMETER_PARAM = 'parameter';

/** The selected key a search string states, or `null`. Pure, like `readFoundationsScope`. */
export function readSelectedParameter(search: string): string | null {
  return readParam(new URLSearchParams(search), PARAMETER_PARAM);
}

/** The selected key, following browser back and forward. */
export function useSelectedParameter(): string | null {
  const [, setNavigations] = useState(0);
  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const search = typeof window === 'undefined' ? '' : window.location.search;
  return useMemo(() => readSelectedParameter(search), [search]);
}

/**
 * Write one scope parameter onto the address bar, preserving everything else on it.
 *
 * `pushState` plus a manual `popstate`, which is load-bearing rather than a workaround:
 * the shell navigates with `pushState` and the browser fires no event for it, so the hook
 * below would never learn that the scope changed. ch04 and ch05 carry the same note.
 */
export function setFoundationsScopeParam(
  name: string, value: string | null, mode: 'push' | 'replace' = 'push',
): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  if (value === null || value.trim() === '') next.delete(name);
  else next.set(name, value);
  const query = next.toString();
  const url = query === '' ? window.location.pathname : `?${query}`;
  // `replace` for a default the screen applied: the back button must not return to a URL
  // that immediately redirects forward again.
  if (mode === 'replace') window.history.replaceState({}, '', url);
  else window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Move to another screen of this chapter, carrying the query string with it.
 *
 * The shell's own navigation carries the query too (`useUrlState.navigate`), so a planner who
 * follows a "see also" link from the registry and comes back finds the date they chose
 * still on the link. Same `pushState` plus `popstate` as above, for the same reason.
 */
export function navigateWithinFoundations(pathname: string, carryQuery = true): void {
  if (typeof window === 'undefined') return;
  // `carryQuery = false` for a link OUT of this chapter (the Documentation screen's section
  // headings): `?asOf=` means something else in ch04, and a scope this chapter chose must not
  // arrive there as if the planner had chosen it.
  window.history.pushState({}, '', carryQuery ? `${pathname}${window.location.search}` : pathname);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** The current scope, following browser back and forward. */
export function useFoundationsScope(): FoundationsScope {
  const [, setNavigations] = useState(0);

  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const search = typeof window === 'undefined' ? '' : window.location.search;
  return useMemo(() => readFoundationsScope(search), [search]);
}
